import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import { createBusiness, startEnrolation, validateCredentials } from 'lib/api';
import { getRutificator } from 'lib/api/business';
import CustomButton from 'lib/designSystem/Button';
import ErrorHelper from 'lib/designSystem/ErrorHelper';
import { handleError } from 'lib/functions/errors';
import { showAlert, validaRut } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';

interface CreateBusinessDialogProps {
  open: boolean;
  onClose: () => void;
  dispatch: Dispatch;
  missingRuts: string[];
  onBusinessCreated?: () => void;
}

interface BusinessFormData {
  rut: string;
  razonSocial: string;
  password: string;
  connectSII: boolean;
  rutSearched: boolean;
  loadingRut: boolean;
  errors: {
    rut?: string;
    razonSocial?: string;
    password?: string;
  };
  showRutNotFound: boolean;
}

const CreateBusinessDialog: React.FC<CreateBusinessDialogProps> = ({
  open,
  onClose,
  dispatch,
  missingRuts,
  onBusinessCreated,
}) => {
  const [currentRutIndex, setCurrentRutIndex] = useState(0);
  const [businessData, setBusinessData] = useState<BusinessFormData>({
    rut: '',
    razonSocial: '',
    password: '',
    connectSII: false,
    rutSearched: false,
    loadingRut: false,
    errors: {},
    showRutNotFound: false,
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open && missingRuts.length > 0) {
      setCurrentRutIndex(0);
      setBusinessData({
        rut: missingRuts[0],
        razonSocial: '',
        password: '',
        connectSII: false,
        rutSearched: false,
        loadingRut: false,
        errors: {},
        showRutNotFound: false,
      });
    }
  }, [open, missingRuts]);

  const handleBuscarRut = async () => {
    setBusinessData((prev) => ({
      ...prev,
      errors: {},
      showRutNotFound: false,
      loadingRut: true,
    }));

    const rutValido = validaRut(businessData.rut);
    if (!rutValido) {
      setBusinessData((prev) => ({
        ...prev,
        errors: { rut: 'El RUT ingresado no es válido' },
        loadingRut: false,
      }));
      return;
    }

    try {
      //const data = await getRutificator(dispatch, [businessData.rut]);
      const razonSocial = 'Razon Social';
      //if (data?.results?.[businessData.rut]?.[0]?.['RAZON_SOCIAL']) {
      //  razonSocial =
      //    data.results[businessData.rut][0]['RAZON_SOCIAL'].toUpperCase();
      //}
      setBusinessData((prev) => ({
        ...prev,
        razonSocial,
        rutSearched: true,
        showRutNotFound: !razonSocial,
        loadingRut: false,
      }));
    } catch (error: unknown) {
      setBusinessData((prev) => ({
        ...prev,
        showRutNotFound: true,
        loadingRut: false,
      }));
      handleError(error);
    }
  };

  const handleSubmit = async () => {
    setBusinessData((prev) => ({ ...prev, errors: {} }));
    setCreating(true);

    const errors: BusinessFormData['errors'] = {};
    const rutValido = validaRut(businessData.rut);
    if (!rutValido) {
      errors.rut = 'El RUT ingresado no es válido';
    }
    if (!businessData.razonSocial) {
      errors.razonSocial = 'La razón social es requerida';
    }
    if (businessData.connectSII && !businessData.password) {
      errors.password = 'La contraseña es requerida';
    }

    if (Object.keys(errors).length > 0) {
      setBusinessData((prev) => ({ ...prev, errors }));
      setCreating(false);
      return;
    }

    try {
      if (businessData.connectSII) {
        await validateCredentials(dispatch, {
          taxPassword: businessData.password,
          taxIdentifier: businessData.rut,
        });
        const response = await createBusiness(dispatch, {
          name: businessData.razonSocial,
          identifier: businessData.rut,
          taxPassword: businessData.password,
          enrolledPath: 'myAccounts',
        });
        await startEnrolation(dispatch, {
          taxPassword: businessData.password,
          taxIdentifier: businessData.rut,
          businessId: response.id,
        });
        showAlert(
          dispatch,
          'success',
          'Estamos ingestando las nuevas facturas. Estas estarán disponibles en algunos minutos',
        );
      } else {
        await createBusiness(dispatch, {
          name: businessData.razonSocial,
          identifier: businessData.rut,
          taxPassword: businessData.password,
        });
        showAlert(
          dispatch,
          'success',
          'Se ha creado un nuevo Cliente. Puedes activarla cuando lo necesites apretando "Actualizar Cliente"',
        );
      }

      // Si hay más RUTs, pasar al siguiente
      if (currentRutIndex < missingRuts.length - 1) {
        const nextIndex = currentRutIndex + 1;
        setCurrentRutIndex(nextIndex);
        setBusinessData({
          rut: missingRuts[nextIndex],
          razonSocial: '',
          password: '',
          connectSII: false,
          rutSearched: false,
          loadingRut: false,
          errors: {},
          showRutNotFound: false,
        });
      } else {
        // Se crearon todas las empresas
        showAlert(
          dispatch,
          'success',
          `Se han creado ${missingRuts.length} empresa(s) exitosamente. Ahora puedes reintentar la importación.`,
        );
        if (onBusinessCreated) {
          onBusinessCreated();
        }
        handleClose();
      }
    } catch (error: unknown) {
      showAlert(
        dispatch,
        'error',
        businessData.connectSII
          ? 'Error al conectar el cliente'
          : 'Error al crear el cliente',
      );
      handleError(error);
    }
    setCreating(false);
  };

  const handleClose = () => {
    setCurrentRutIndex(0);
    setBusinessData({
      rut: '',
      razonSocial: '',
      password: '',
      connectSII: false,
      rutSearched: false,
      loadingRut: false,
      errors: {},
      showRutNotFound: false,
    });
    setCreating(false);
    onClose();
  };

  const isFormValid = () => {
    if (
      !businessData.rut ||
      !validaRut(businessData.rut) ||
      businessData.errors.rut
    )
      return false;
    if (!businessData.razonSocial || businessData.errors.razonSocial)
      return false;
    if (
      businessData.connectSII &&
      (!businessData.password || businessData.errors.password)
    )
      return false;
    return true;
  };

  const remainingCount = missingRuts.length - currentRutIndex - 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
        Crear Empresa{missingRuts.length > 1 ? 's' : ''}
      </DialogTitle>
      <DialogContent>
        {missingRuts.length > 1 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Creando empresa {currentRutIndex + 1} de {missingRuts.length}
            {remainingCount > 0 &&
              ` (${remainingCount} restante${remainingCount > 1 ? 's' : ''})`}
          </Alert>
        )}
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            margin="dense"
            label="RUT (sin puntos, con guión)"
            fullWidth
            value={businessData.rut}
            onChange={(e) =>
              setBusinessData((prev) => ({
                ...prev,
                rut: e.target.value.toUpperCase(),
                rutSearched: false,
                razonSocial: '',
                errors: { ...prev.errors, rut: '' },
                showRutNotFound: false,
              }))
            }
            disabled={businessData.rutSearched || businessData.loadingRut}
            error={!!businessData.errors.rut}
            helperText={<ErrorHelper message={businessData.errors.rut || ''} />}
            autoFocus
          />
          {!businessData.rutSearched && (
            <CustomButton
              onClick={handleBuscarRut}
              color="primary"
              disabled={businessData.loadingRut || !businessData.rut}
              fullWidth
            >
              {businessData.loadingRut ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                <>
                  <SearchIcon sx={{ mr: 1 }} />
                  Buscar RUT
                </>
              )}
            </CustomButton>
          )}
          {businessData.rutSearched && (
            <>
              <TextField
                margin="dense"
                label="Razón Social"
                fullWidth
                value={businessData.razonSocial}
                onChange={(e) =>
                  setBusinessData((prev) => ({
                    ...prev,
                    razonSocial: e.target.value.toUpperCase(),
                    errors: { ...prev.errors, razonSocial: '' },
                  }))
                }
                inputProps={{ style: { textTransform: 'uppercase' } }}
                required
                error={!!businessData.errors.razonSocial}
                helperText={
                  <ErrorHelper
                    message={businessData.errors.razonSocial || ''}
                  />
                }
              />
              {businessData.connectSII && (
                <TextField
                  margin="dense"
                  label="Contraseña"
                  value={businessData.password}
                  onChange={(e) =>
                    setBusinessData((prev) => ({
                      ...prev,
                      password: e.target.value,
                      errors: { ...prev.errors, password: '' },
                    }))
                  }
                  type="password"
                  fullWidth
                  required
                  error={!!businessData.errors.password}
                  helperText={
                    <ErrorHelper message={businessData.errors.password || ''} />
                  }
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={businessData.connectSII}
                    onChange={(e) =>
                      setBusinessData((prev) => ({
                        ...prev,
                        connectSII: e.target.checked,
                        password: e.target.checked ? prev.password : '',
                        errors: { ...prev.errors, password: '' },
                      }))
                    }
                    color="primary"
                  />
                }
                label="Conectar con SII"
              />
              {businessData.showRutNotFound && (
                <Alert severity="warning">
                  No se encontró razón social para este RUT. Puedes ingresarla
                  manualmente.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <CustomButton
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          disabled={creating}
        >
          Cancelar
        </CustomButton>
        {businessData.rutSearched && (
          <CustomButton
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={businessData.loadingRut || !isFormValid() || creating}
          >
            {creating ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              `Crear${remainingCount > 0 ? ' y Continuar' : ''}`
            )}
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateBusinessDialog;
